import cn from 'classnames';
import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Grid, Layout } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';

import { Currency } from '@src/@types/currency';
import type { ITermRange } from '@src/@types/ranges';
import { TERMS } from '@src/@types/ranges';
import { BestProducts } from '@src/components/item/BestProducts';
import { PRODUCT_DESCRIPTION_ANCHOR } from '@src/constants/common';
import { ListKeys } from '@src/constants/microcredits';
import {
    getAmountRangeToString,
    getRateRangeString,
    humanizeTermRange,
    scrollToElementById,
} from '@src/helpers/common';
import { emptyArray } from '@src/helpers/emptyArray';
import { getOrganizationPageLink } from '@src/helpers/links';
import { selectItemSource, useCard } from '@src/reducers/product';

import { Logo } from '../../Logotype';
import { ActionButton } from '../ActionButton';
import { ArrowButton } from '../ArrowButton';

import Feature from './Feature';
import { List } from './List';
import styles from './styles.module.scss';

interface IProps {
    className?: string;
}

export const Summary: React.FunctionComponent<IProps> = memo((props: IProps) => {
    const { className } = props;
    const isMobile = useIsMobile();
    const card = useCard();
    const { organization: { name, logotypes, alias } = {} } = card;
    const logoLink = { href: alias && getOrganizationPageLink(alias), withAdvertising: false };

    const handleScrollToDescription = useCallback(() => {
        scrollToElementById(PRODUCT_DESCRIPTION_ANCHOR);
    }, []);

    const { rates: { RUB: rates = emptyArray } = {} } = useSelector(selectItemSource) ?? {};

    const features = React.useMemo(() => {
        const amountMin = Math.min(...rates.map(({ amount }) => amount.from ?? 0));
        const amountMax = Math.max(...rates.map(({ amount }) => amount.to ?? 0));
        const termMin = rates
            .flatMap(({ periods }) =>
                periods.map(({ term: { fromValue, fromUnit, from } }) => ({
                    from: from ?? 'P0D',
                    fromUnit,
                    fromValue: fromValue ?? 0,
                })),
            )
            .reduce((min, value) => (min.fromValue > value.fromValue ? value : min), {
                fromValue: Infinity,
                from: 'P0D',
                fromUnit: TERMS.days,
            });
        const termMax = rates
            .flatMap(({ periods }) =>
                periods.map(({ term: { toValue, toUnit, to } }) => ({
                    to: to ?? 'P0D',
                    toUnit,
                    toValue: toValue ?? 0,
                })),
            )
            .reduce((max, value) => (max.toValue < value.toValue ? value : max), {
                toValue: 0,
                to: 'P0D',
                toUnit: TERMS.days,
            });
        const term = { ...termMin, ...termMax } as ITermRange;
        const rateMin = Math.min(...rates.flatMap(({ periods }) => periods.map(({ rate }) => rate.from ?? 0)));
        const rateMax = Math.max(...rates.flatMap(({ periods }) => periods.map(({ rate }) => rate.to ?? 0)));
        return [
            {
                title: 'Сумма',
                data: getAmountRangeToString({
                    amountRange: { from: amountMin, to: amountMax },
                    currency: Currency.RUB,
                    prefix: true,
                }),
            },
            {
                title: 'Срок',
                data: humanizeTermRange(term),
            },
            {
                title: 'Ставка',
                data: getRateRangeString({ from: rateMin, to: rateMax }),
            },
        ];
    }, [rates]);

    if (!features.length) {
        return null;
    }

    return (
        <div className={cn(styles.wrapper, className)}>
            <div className={styles.top}>
                <div className={styles.organization}>
                    <Logo
                        link={logoLink}
                        logo={logotypes?.android}
                        isPartner={card.isBankPartner || card.advertising?.isPartner}
                    />
                    <div className={styles.organization_name}>{name}</div>
                </div>
            </div>

            <div className={styles.inner}>
                <div className={styles.container}>
                    <Layout className={styles.layout}>
                        <div className={styles.feature}>
                            {features.map(
                                ({ data, title }) =>
                                    Boolean(data) && <Feature title={title} data={data} key={`${data}${title}`} />,
                            )}
                        </div>
                        <div>
                            <List card={card} />
                        </div>
                    </Layout>
                    {isMobile ? (
                        <div>
                            {!card.advertising?.monetization && (
                                <Layout className={cn(styles.layout)}>
                                    <BestProducts listKey={ListKeys.BESTPRODUCTS} />
                                </Layout>
                            )}
                        </div>
                    ) : (
                        <Layout
                            className={cn(styles.layout, { [styles.footer_layout]: card.advertising?.monetization })}
                        >
                            {!card.advertising?.monetization && <BestProducts listKey={ListKeys.BESTPRODUCTS} />}
                            <Grid.Row className={styles.footer} gutter={20} align="middle" justify="space-between">
                                <Grid.Col span={3}>
                                    <ArrowButton
                                        text="Подробные условия"
                                        direction="down"
                                        onClick={handleScrollToDescription}
                                    />
                                </Grid.Col>
                                <Grid.Col span={3}>
                                    <ActionButton />
                                </Grid.Col>
                            </Grid.Row>
                        </Layout>
                    )}
                </div>
            </div>
        </div>
    );
});
